// Weitere Seiten des Bereichs "Steuerberater": Vergleich einzelner Konten ueber mehrere Monate, fehlende/unsichere Buchungen.
// Nur lesende Aufrufe der bestehenden Endpunkte; nichts wird veraendert oder uebermittelt.
import { useState, useEffect } from "react";
import THEME from "../../../../theme";
import { fs } from "../../utils/styles";
import { pbApi } from "../../../belege/belegeApi";
import { Meldung, Chip } from "../../../belege/ui";
import { Seitenkopf, PbBanner, Abschnitt, kachelRaster, Kachel, geld, useBreite, BREAKPOINT_KLEIN } from "../../ui/Bausteine";
import { Buchungen, Uebersicht } from "../belege/BelegeParallelbuch";
import { STATUS, useVergleichsDaten } from "./Monatsvergleich";

const ymText = (ym) => `${String(ym).slice(4)}/${String(ym).slice(0, 4)}`;

export function Kontovergleich() {
  const { monate, fehler: f0 } = useVergleichsDaten();
  const breite = useBreite();
  const [konto, setKonto] = useState("");
  const [anzahl, setAnzahl] = useState(12);
  const [zeilen, setZeilen] = useState(null);
  const [fehler, setFehler] = useState("");
  const [laed, setLaed] = useState(false);
  const [fortschritt, setFortschritt] = useState(0);

  const laden = async () => {
    const nr = konto.trim();
    if (!/^\d{4,5}$/.test(nr) || !monate?.length) { setFehler("Bitte eine Kontonummer mit 4 oder 5 Ziffern eingeben."); return; }
    setFehler(""); setLaed(true); setZeilen(null); setFortschritt(0);
    const ziel = monate.slice(-anzahl);
    const erg = [];
    try {
      for (let i = 0; i < ziel.length; i += 3) {
        const teil = await Promise.all(ziel.slice(i, i + 3).map(ym => pbApi.vergleich(ym).then(v => ({ ym, v }))));
        teil.forEach(({ ym, v }) => erg.push({ ym, z: v.zeilen.find(x => x.art === "konto" && x.schluessel === nr) || null, pb: v.zusammenfassung.pb_buchungen_im_monat }));
        setFortschritt(Math.min(i + 3, ziel.length));
      }
      setZeilen(erg);
    } catch (e) { setFehler(e.message); }
    setLaed(false);
  };

  const summe = (feld) => (zeilen || []).reduce((s, x) => s + (x.z && x.z[feld] !== null && x.z[feld] !== undefined ? Number(x.z[feld]) : 0), 0);
  const ohne = (zeilen || []).filter(x => !x.z).length;
  const bezeichnung = (zeilen || []).map(x => x.z?.bezeichnung).find(Boolean);
  const mitPb = (zeilen || []).filter(x => x.pb > 0).length;
  return (
    <div style={{ color: THEME.colors.text }}>
      <PbBanner />
      <Seitenkopf titel="Vergleich einzelner Konten" untertitel="Ein SKR03-Konto über mehrere Monate: Bewegung laut ComeFit gegen Bewegung laut Kanzlei." />
      <Meldung fehler={fehler || f0} />
      <Abschnitt>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "end" }}>
          <label style={{ flex: "1 1 160px" }}><span style={fs.label}>SKR03-Konto</span><input style={{ ...fs.input, width: "100%" }} inputMode="numeric" placeholder="z. B. 4920" value={konto} onChange={e => setKonto(e.target.value)} onKeyDown={e => e.key === "Enter" && laden()} /></label>
          <label style={{ flex: "1 1 160px" }}><span style={fs.label}>Zeitraum</span>
            <select style={{ ...fs.input, width: "100%" }} value={anzahl} onChange={e => setAnzahl(Number(e.target.value))}>
              {[6, 12, 24].map(n => <option key={n} value={n}>letzte {n} Monate</option>)}<option value={999}>alle Kanzlei-Monate</option>
            </select></label>
          <button style={fs.btn} onClick={laden} disabled={laed}>{laed ? `Lädt… (${fortschritt})` : "Vergleichen"}</button>
        </div>
        {monate && monate.length === 0 && <div style={{ color: THEME.colors.warning, marginTop: 10 }}>Es sind keine Kanzleidaten importiert – kein Vergleich möglich.</div>}
      </Abschnitt>
      {zeilen && (
        <>
          <div style={kachelRaster}>
            <Kachel titel={`Summe ComeFit${bezeichnung ? ` – ${bezeichnung}` : ""}`} wert={geld(summe("pb_wert"))} hinweis={`${mitPb} von ${zeilen.length} Monaten mit ComeFit-Buchungen`} />
            <Kachel titel="Summe Kanzlei" wert={geld(summe("kanzlei_wert"))} hinweis={ohne ? `${ohne} Monat(e) ohne Bewegung/Zeile für dieses Konto` : undefined} />
            <Kachel titel="Differenz (nur Monate mit beiden Werten)" wert={geld(zeilen.reduce((s, x) => s + (x.z && x.z.differenz !== null ? Number(x.z.differenz) : 0), 0), true)} farbe={THEME.colors.danger} hinweis="Summe der Monatsdifferenzen – kein Beleg für Richtigkeit" />
          </div>
          {mitPb < zeilen.length && <div role="status" style={{ color: THEME.colors.warning, marginBottom: 10 }}>⚠️ In {zeilen.length - mitPb} der {zeilen.length} Monate gibt es keine ComeFit-Buchungen – der Vergleich ist für diese Monate nicht aussagekräftig.</div>}
          <Abschnitt titel={`Konto ${konto.trim()} je Monat`}>
            {breite >= BREAKPOINT_KLEIN ? (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr><th style={fs.th}>Monat</th><th style={{ ...fs.th, textAlign: "right" }}>ComeFit</th><th style={{ ...fs.th, textAlign: "right" }}>Kanzlei</th><th style={{ ...fs.th, textAlign: "right" }}>Differenz</th><th style={fs.th}>Status</th></tr></thead>
                <tbody>{zeilen.map(x => (
                  <tr key={x.ym}><td style={fs.td}>{ymText(x.ym)}</td>
                    <td style={{ ...fs.td, textAlign: "right" }}>{x.z ? geld(x.z.pb_wert) : "–"}</td><td style={{ ...fs.td, textAlign: "right" }}>{x.z ? geld(x.z.kanzlei_wert) : "–"}</td>
                    <td style={{ ...fs.td, textAlign: "right" }}>{x.z ? geld(x.z.differenz) : "–"}</td>
                    <td style={fs.td}>{x.z ? <Chip text={STATUS[x.z.status]?.text || x.z.status} farbe={STATUS[x.z.status]?.farbe} /> : <span style={{ color: THEME.colors.textMuted }}>keine Bewegung bei beiden</span>}</td></tr>
                ))}</tbody>
              </table>
            ) : (
              <div style={{ display: "grid", gap: 8 }}>{zeilen.map(x => (
                <div key={x.ym} style={{ border: `1px solid ${THEME.colors.border}`, borderRadius: THEME.radius.md, padding: "8px 10px" }}>
                  <strong>{ymText(x.ym)}</strong> {x.z ? <Chip text={STATUS[x.z.status]?.text || x.z.status} farbe={STATUS[x.z.status]?.farbe} /> : <span style={{ color: THEME.colors.textMuted }}>keine Bewegung</span>}
                  {x.z && <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", fontSize: THEME.fontSize.base }}><span style={{ color: THEME.colors.textMuted }}>ComeFit</span><span style={{ textAlign: "right" }}>{geld(x.z.pb_wert)}</span><span style={{ color: THEME.colors.textMuted }}>Kanzlei</span><span style={{ textAlign: "right" }}>{geld(x.z.kanzlei_wert)}</span><span style={{ color: THEME.colors.textMuted }}>Differenz</span><span style={{ textAlign: "right" }}>{geld(x.z.differenz)}</span></div>}
                </div>
              ))}</div>
            )}
          </Abschnitt>
        </>
      )}
    </div>
  );
}

export function UnsicherePage({ onNavigiere }) {
  const [fehler, setFehler] = useState("");
  const [hinweis, setHinweis] = useState("");
  const [neu, setNeu] = useState(0);
  useEffect(() => { setHinweis(""); }, [neu]);
  return (
    <div style={{ color: THEME.colors.text }}>
      <PbBanner />
      <Seitenkopf titel="Fehlende oder unsichere Buchungen" untertitel="Was in der Parallelbuchhaltung noch nicht belegt, nicht zugeordnet oder unsicher ist. Unsichere Buchungen gelten nie automatisch als korrekt." />
      <Meldung fehler={fehler} hinweis={hinweis} />
      <Uebersicht setFehler={setFehler} />
      <Abschnitt titel="Unsichere und ungeprüfte Buchungen">
        <Buchungen setFehler={setFehler} setHinweis={setHinweis} neuLaden={() => setNeu(n => n + 1)} start={{ sicherheit: "unsicher", ungeprueft: true }} />
      </Abschnitt>
      {onNavigiere && <button style={fs.btnGray} onClick={() => onNavigiere("belege", "kontierung")}>Zur Kontierung der Belege</button>}
    </div>
  );
}
